// src/api/features/hotel-bookings/queries.ts

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminBookingKeys } from "@/api/features/bookings/queries";

import { adminCancelHotelBooking, adminGetHotelCancellation, adminGetHotelVoucher } from "./api";
import type { AdminHotelCancellationOverview, AdminHotelVoucher } from "./types";

export const AdminHotelBookingKeys = {
  all: ["adminHotelBookings"] as const,
  voucher: (bookingId: number | string) =>
    [...AdminHotelBookingKeys.all, "voucher", String(bookingId)] as const,
  cancellation: (bookingId: number | string) =>
    [...AdminHotelBookingKeys.all, "cancellation", String(bookingId)] as const,
};

function isValidId(bookingId: number | string | null | undefined): bookingId is number | string {
  return bookingId !== null && bookingId !== undefined && String(bookingId).trim() !== "";
}

export function useAdminHotelVoucher(bookingId: number | string | null | undefined) {
  return useQuery<AdminHotelVoucher, Error>({
    queryKey: isValidId(bookingId)
      ? AdminHotelBookingKeys.voucher(bookingId)
      : [...AdminHotelBookingKeys.all, "voucher", "disabled"],
    queryFn: () => adminGetHotelVoucher(String(bookingId)),
    enabled: isValidId(bookingId),
    staleTime: 15_000,
    retry: false,
  });
}

export function useAdminHotelCancellation(bookingId: number | string | null | undefined) {
  return useQuery<AdminHotelCancellationOverview, Error>({
    queryKey: isValidId(bookingId)
      ? AdminHotelBookingKeys.cancellation(bookingId)
      : [...AdminHotelBookingKeys.all, "cancellation", "disabled"],
    queryFn: () => adminGetHotelCancellation(String(bookingId)),
    enabled: isValidId(bookingId),
    // The backend re-polls the supplier at most every 30s; matching that keeps
    // a pending cancellation current without hammering the endpoint.
    staleTime: 30_000,
    retry: false,
  });
}

export function useAdminCancelHotelBooking(bookingId: number | string) {
  const queryClient = useQueryClient();

  return useMutation<AdminHotelCancellationOverview, Error, { reason: string }>({
    mutationFn: (body) => adminCancelHotelBooking(bookingId, body),
    onSuccess: (overview) => {
      queryClient.setQueryData(AdminHotelBookingKeys.cancellation(bookingId), overview);
      // Booking status, list rows and tab counts all move on a cancellation.
      void queryClient.invalidateQueries({ queryKey: AdminHotelBookingKeys.voucher(bookingId) });
      void queryClient.invalidateQueries({ queryKey: AdminBookingKeys.all });
    },
  });
}
